import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  CreditCard, Lock, Shield, CheckCircle2, Loader2,
  Wifi, AlertCircle
} from 'lucide-react';

const PaymentGateway = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const referenceId = searchParams.get('ref');
  const amount = searchParams.get('amount');
  const planName = searchParams.get('plan');

  const [cardDetails, setCardDetails] = useState({
    cardNumber: '',
    cardName: '',
    expiry: '',
    cvv: ''
  });

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    return parts.length ? parts.join(' ') : value;
  };

  const formatExpiry = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return v.substring(0, 2) + '/' + v.substring(2, 4);
    }
    return v;
  };

  const handleInputChange = (e) => {
    let { name, value } = e.target;
    
    if (name === 'cardNumber') {
      value = formatCardNumber(value);
    } else if (name === 'expiry') {
      value = formatExpiry(value);
    } else if (name === 'cvv') {
      value = value.replace(/[^0-9]/gi, '').substring(0, 3);
    }
    
    setCardDetails({
      ...cardDetails,
      [name]: value
    });
  };

  const handlePayment = async () => {
    if (!cardDetails.cardNumber || !cardDetails.cardName || !cardDetails.expiry || !cardDetails.cvv) {
      setError('Please fill in all card details');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Confirm payment
      const response = await fetch(`${API_URL}/api/checkout/confirm-payment/${referenceId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        // Redirect to success page after 2 seconds
        setTimeout(() => {
          navigate(`/payment-success?ref=${referenceId}`);
        }, 2000);
      } else {
        setError('Payment failed. Please try again.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error('Payment error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-900 via-green-800 to-green-900 flex items-center justify-center p-6">
        <Card className="max-w-md w-full border-0 bg-white/10 backdrop-blur-sm">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-green-500 rounded-full flex items-center justify-center animate-bounce">
              <CheckCircle2 className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Payment Successful!</h1>
            <p className="text-green-200 mb-4">Your payment of ₹{amount} has been processed successfully.</p>
            <p className="text-sm text-green-300">Reference ID: {referenceId}</p>
            <p className="text-sm text-green-400 mt-4">Redirecting to confirmation page...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-10 px-6">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-block p-4 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl mb-4">
            <Shield className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">Secure Payment</h1>
          <p className="text-blue-200 mt-1">Powered by ZOHO Payments</p>
        </div>

        {/* Order Summary */}
        <Card className="border-0 bg-white/10 backdrop-blur-sm mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <Wifi className="h-6 w-6 text-red-400" />
                </div>
                <div>
                  <h3 className="font-bold text-white">{planName} Plan</h3>
                  <p className="text-sm text-gray-400">Netix Fibernet</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-black text-white">₹{amount}</div>
                <div className="text-xs text-gray-400">Ref: {referenceId}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Form */}
        <Card className="border-0 bg-white/10 backdrop-blur-sm">
          <CardContent className="p-6 space-y-5">
            <div>
              <label className="text-sm text-gray-300 mb-1 block">Card Number</label>
              <div className="relative">
                <Input
                  name="cardNumber"
                  value={cardDetails.cardNumber}
                  onChange={handleInputChange}
                  placeholder="1234 5678 9012 3456"
                  maxLength={19}
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-500 pl-12"
                />
                <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              </div>
            </div>

            <div>
              <label className="text-sm text-gray-300 mb-1 block">Cardholder Name</label>
              <Input
                name="cardName"
                value={cardDetails.cardName}
                onChange={handleInputChange}
                placeholder="JOHN DOE"
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-500 uppercase"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-300 mb-1 block">Expiry Date</label>
                <Input
                  name="expiry"
                  value={cardDetails.expiry}
                  onChange={handleInputChange}
                  placeholder="MM/YY"
                  maxLength={5}
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                />
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1 block">CVV</label>
                <Input
                  name="cvv"
                  value={cardDetails.cvv}
                  onChange={handleInputChange}
                  placeholder="123"
                  maxLength={3}
                  type="password"
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <span className="text-red-300 text-sm">{error}</span>
              </div>
            )}

            <Button
              onClick={handlePayment}
              disabled={loading}
              className="w-full h-14 text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Processing Payment...
                </>
              ) : (
                <>
                  <Lock className="h-5 w-5 mr-2" />
                  Pay ₹{amount}
                </>
              )}
            </Button>

            {/* Security Info */}
            <div className="flex items-center justify-center gap-4 pt-4 border-t border-white/10">
              <div className="flex items-center gap-1 text-xs text-gray-400">
                <Lock className="h-3 w-3" />
                <span>256-bit SSL</span>
              </div>
              <div className="flex items-center gap-1 text-xs text-gray-400">
                <Shield className="h-3 w-3" />
                <span>PCI Compliant</span>
              </div>
            </div>

            <p className="text-xs text-center text-gray-500">
              This is a demo payment gateway. Use any card details for testing.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentGateway;
